// Route state-machine UI hook (D3e UI). Extracted from
// useLogisticsRouteDetail.ts as the prep split for D3g UI per the
// file-size hygiene rule (the detail hook was at 303 lines coming
// out of D3e UI -- the warning band -- and D3g UI adds the per-stop
// edits hook + StopRow modifications). Split by concern: this hook
// owns the route-level transition affordance; useLogisticsRouteDetail
// keeps the detail fetch + add-stop form; the D3g UI follow-on will
// add a third sibling hook for per-stop edits.
//
// Mirrors the backend's VALID_TRANSITIONS map in
// server/mfg-services/routesService.ts. Kept in lock-step manually --
// changing one without the other yields buttons that the server then
// rejects with a 409, but the server stays the source of truth. If we
// ever expand the state machine, both sides need the same edit.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  updateLogisticsRouteState,
  type LogiRouteState,
} from "@/lib/logistics";

const VALID_TRANSITIONS: Readonly<Record<LogiRouteState, readonly LogiRouteState[]>> = {
  planned: ["scheduled", "cancelled"],
  scheduled: ["in_transit", "cancelled"],
  in_transit: ["delivered", "cancelled"],
  delivered: [],
  cancelled: [],
};

export function useLogisticsRouteStateTransitions(
  routeId: string | null,
  currentState: LogiRouteState | null,
  reload: () => Promise<void>
) {
  // One in-flight transition at a time; the busy slot also disables
  // the whole button group so a hammered button can't issue parallel
  // transitions.
  const [transitionBusy, setTransitionBusy] = useState<LogiRouteState | null>(null);
  const [transitionError, setTransitionError] = useState<string | null>(null);

  // Reset on route change so a stale error from the previous route
  // doesn't bleed into the newly-selected one.
  useEffect(() => {
    setTransitionBusy(null);
    setTransitionError(null);
  }, [routeId]);

  const allowedTransitions = useMemo<readonly LogiRouteState[]>(
    () => (currentState ? VALID_TRANSITIONS[currentState] : []),
    [currentState]
  );

  const submitTransition = useCallback(
    async (
      nextState: LogiRouteState,
      // Callback fired on success so the parent panel can also refresh
      // the route LIST (the state chip color changes there too). Detail
      // refresh is handled by the passed-in reload().
      onAfterSuccess?: () => void
    ): Promise<{ ok: boolean; error?: string }> => {
      if (!routeId) return { ok: false, error: "no route selected" };
      if (!currentState) return { ok: false, error: "route not loaded yet" };
      if (!VALID_TRANSITIONS[currentState].includes(nextState)) {
        const exits = VALID_TRANSITIONS[currentState];
        const label = exits.length === 0 ? "(terminal)" : exits.join(", ");
        const msg = `Cannot transition '${currentState}' -> '${nextState}'. Allowed: ${label}.`;
        setTransitionError(msg);
        return { ok: false, error: msg };
      }
      setTransitionBusy(nextState);
      setTransitionError(null);
      const res = await updateLogisticsRouteState({
        route_id: routeId,
        route_state: nextState,
      });
      setTransitionBusy(null);
      if (!res.ok) {
        setTransitionError(res.error);
        return { ok: false, error: res.error };
      }
      await reload();
      if (onAfterSuccess) onAfterSuccess();
      return { ok: true };
    },
    [routeId, currentState, reload]
  );

  return {
    allowedTransitions,
    transitionBusy,
    transitionError,
    submitTransition,
  };
}
